import React from 'react';
import {
  Box, Button, Divider, Paper, Stack, TextField, Typography,
} from '@mui/material';

import { applyTaskAction, fetchTaskActions } from './taskprogress.apis';
import { ITaskProgress } from './taskprogress.models';

type TaskAction = {
  rid?: number;
  acn?: string;
  acm?: string;
  acd?: string;
  fsc?: string;
  tsc?: string;
  nts?: string;
};

type Props = {
  task?: ITaskProgress;
  onChanged?: () => void;
};

type State = {
  rows: TaskAction[];
  notes: string;
  saving: boolean;
};

export default class TaskActionTimeline extends React.PureComponent<Props, State> {
  state: State = {
    rows: [],
    notes: '',
    saving: false,
  };

  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.task?.rid !== this.props.task?.rid) {
      this.load();
      this.setState({ notes: '' });
    }
  }

  load = async () => {
    if (!this.props.task?.rid) return;
    const rows = await fetchTaskActions(this.props.task.rid);
    this.setState({ rows });
  };

  action = async (actioncode: string, tostateid?: number) => {
    const { task } = this.props;
    if (!task?.rid) return;

    this.setState({ saving: true });

    await applyTaskAction({
      tpi: task.rid,
      acn: actioncode,
      tsi: tostateid ?? null,
      tui: null,
      nts: this.state.notes,
      exi: {},
    });

    this.setState({ saving: false, notes: '' });
    await this.load();
    this.props.onChanged?.();
  };

  render() {
    const { task } = this.props;
    const { rows, notes, saving } = this.state;

    if (!task?.rid) {
      return (
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography color="text.secondary">
            Save progress before workflow actions are available.
          </Typography>
        </Paper>
      );
    }

    return (
      <Paper elevation={1} sx={{ p: 2 }}>
        <Typography variant="subtitle1">Workflow Actions</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Drive execution forward through validation, correction, escalation, or closure.
        </Typography>

        <TextField
          label="Action Notes"
          multiline
          minRows={2}
          fullWidth
          value={notes}
          onChange={(e) => this.setState({ notes: e.target.value })}
          sx={{ mb: 2 }}
        />

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button size="small" variant="contained" disabled={saving} onClick={() => this.action('SUBMIT_PROGRESS', 2)}>
            Submit
          </Button>
          <Button size="small" variant="outlined" disabled={saving} onClick={() => this.action('REQUEST_EVIDENCE', 3)}>
            Request Evidence
          </Button>
          <Button size="small" variant="outlined" disabled={saving} onClick={() => this.action('RETURN_PROGRESS', 4)}>
            Return
          </Button>
          <Button size="small" variant="contained" disabled={saving} onClick={() => this.action('APPROVE_PROGRESS', 5)}>
            Approve
          </Button>
          <Button size="small" color="warning" variant="outlined" disabled={saving} onClick={() => this.action('ESCALATE_DELAY', 3)}>
            Escalate
          </Button>
          <Button size="small" color="success" variant="outlined" disabled={saving} onClick={() => this.action('CLOSE_TASK', 8)}>
            Close
          </Button>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack spacing={1}>
          {rows.map((r) => (
            <Box key={r.rid}>
              <Typography variant="body2">
                <b>{r.acn}</b> {r.fsc && r.tsc ? `(${r.fsc} → ${r.tsc})` : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {r.acm || 'User'} · {r.acd}
              </Typography>
              {r.nts && (
                <Typography variant="body2" color="text.secondary">
                  {r.nts}
                </Typography>
              )}
            </Box>
          ))}

          {rows.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No workflow actions yet.
            </Typography>
          )}
        </Stack>
      </Paper>
    );
  }
}